import { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';

import { detectBankFromIban } from '@libs/helpers';
import { BANK_LABELS, Button, Typography } from '@libs/components';
import { getCardExpirationDate } from '@libs/hooks';

import {
  useApiResponse,
  useCardDetector,
  useModal,
  useShowToast,
  useTranslation,
} from 'src/hooks';
import { BANK_ICONS, ResultFeedbackType } from 'src/constants';
import {
  getGeneralTransactionsDetailsQuery,
  saveTransactionMethodMutation,
} from 'src/gql';
import { hashCardMask } from 'src/helpers';

import AssetWithNetworkIcon from '../asset-with-network-icon/AssetWithNetworkIcon';
import EditItemNameModalContent from '../edit-item-name-modal-content/EditItemNameModalContent';

import {
  ActionsContainer,
  BankCard,
  BankCardIbanWrapper,
  BankCardIconAndDate,
  BankImage,
  BankInfoWrapper,
  Card,
  CardWrapper,
  CenterText,
  CryptoTransactionCenterText,
  CryptoTransactionCenterTextWrapper,
  GradientWrapper,
  IconWrapper,
  Image,
  ModalContent,
  SaveTextWrapper,
  TransactionContainer,
} from './styles';

type FeedbackAdditionalActionProps = {
  onCancel: () => void;
  transactionId: number;
};
const FeedbackAdditionalAction: React.FC<FeedbackAdditionalActionProps> = ({
  onCancel,
  transactionId,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const { cardDetector } = useCardDetector();

  const { Dialog, openModal, closeModal } = useModal();
  const [addressName, setAddressName] = useState('');

  const res = useQuery(getGeneralTransactionsDetailsQuery, {
    variables: { record: { transactionId } },
    skip: typeof transactionId !== 'number',
  });
  const { response, loading: responseLoading } = useApiResponse(res);

  const transaction = response?.getGeneralTransactionDetails?.transaction;
  const destinationAddress =
    transaction?.cryptoWithdrawalRequest?.destinationAddress;
  const network =
    response?.getGeneralTransactionDetails.assetSupportedNetwork?.network;
  const isCryptoTransaction = !!network;
  const asset =
    response?.getGeneralTransactionDetails.assetSupportedNetwork?.asset;
  const canSave = response?.getGeneralTransactionDetails?.canSave;

  const bankCard = transaction?.cardPayment?.card;

  const bankCardMask = bankCard?.cardMask;

  const isCardTransaction = !!bankCard && !bankCard?.isSaved;

  const { cardIcon: bankCardIcon, cardProvider } = cardDetector(
    bankCardMask || ''
  );

  const { expirationString: expiration } = getCardExpirationDate(
    bankCard?.cardExpiry || ''
  );

  const hashedCardMask = hashCardMask(bankCardMask, '****');

  const [saveTransactionMethod, saveRes] = useMutation(
    saveTransactionMethodMutation
  );
  const { errorMessage, clearErrorMessage, loading } = useApiResponse(
    saveRes,
    () => {
      let successText;

      if (isCryptoTransaction) {
        successText = t('result_feedback_wallet_save_success');
      } else if (isCardTransaction) {
        successText = t('result_feedback_card_save_success');
      } else {
        successText = t('result_feedback_iban_save_success');
      }

      showToast({
        type: ResultFeedbackType.SUCCESS,
        text1: successText,
      });
      onCancel();
    }
  );

  useEffect(() => {
    if (errorMessage) {
      showToast({
        type: ResultFeedbackType.ERROR,
      });
    }
  }, [errorMessage]);

  const handleSaveTransaction = () => {
    clearErrorMessage();
    if (!transactionId) return;
    saveTransactionMethod({
      variables: {
        record: {
          transactionId: +transactionId,
          name: addressName ? addressName : undefined,
        },
      },
    });
    closeModal();
  };

  // NOTE: BANK
  const iban = transaction?.bankwireWithdrawal?.iban;
  const bank = iban ? detectBankFromIban(iban) : undefined;
  const BankIcon = bank ? BANK_ICONS[bank?.code] : undefined;
  const bankImage = (
    <IconWrapper>
      {typeof BankIcon === 'string' ? <BankImage src={BankIcon} /> : BankIcon}
    </IconWrapper>
  );
  const bankLabel = bank?.code ? t(BANK_LABELS[bank?.code]) : bank?.name;
  // EOF BANK

  const iconText = isCryptoTransaction
    ? `${asset?.name} - ${network.name}`
    : bankLabel;

  const image = isCryptoTransaction ? (
    <IconWrapper>
      <AssetWithNetworkIcon
        assetIconUrl={asset?.iconUrl}
        networkIconUrl={network.iconUrl}
      />
    </IconWrapper>
  ) : (
    bankImage
  );

  const modalTitle = useMemo(() => {
    if (isCryptoTransaction) {
      return t('result_feedback_save_wallet_address');
    } else if (isCardTransaction) {
      return t('deposit_save_card');
    } else {
      return t('result_feedback_save_account');
    }
  }, [isCryptoTransaction, isCardTransaction, t]);

  const mainDescription = useMemo(() => {
    if (isCryptoTransaction) {
      return t('result_feedback_save_crypto_account');
    } else if (isCardTransaction) {
      return t('result_feedback_save_card');
    } else {
      return t('result_feedback_save_iban');
    }
  }, [isCryptoTransaction, isCardTransaction, t]);

  const modalDescription = isCryptoTransaction
    ? t('result_feedback_new_wallet_name')
    : t('result_feedback_new_account');

  if (responseLoading || !response || !canSave) return null;

  const handleSave = () => {
    if (isCardTransaction) {
      handleSaveTransaction();
    } else {
      openModal();
    }
  };
  return (
    <GradientWrapper>
      <TransactionContainer>
        {isCardTransaction ? (
          <BankCard>
            <BankCardIconAndDate>
              <Image src={bankCardIcon} />
              <Typography
                type="caption"
                fontFamily="primaryRegular"
                color={colors.text.secondary}
              >
                {t('common_expiration', { expiration })}
              </Typography>
            </BankCardIconAndDate>
            <BankCardIbanWrapper>
              <Typography
                type="subHeadline"
                fontFamily="primaryMedium"
              >{`${cardProvider} ${hashedCardMask}`}</Typography>
            </BankCardIbanWrapper>
          </BankCard>
        ) : (
          <Card>
            <CardWrapper>
              <BankInfoWrapper>
                {image}
                <Typography
                  color={colors.text.secondary}
                  type="caption"
                  fontFamily="primaryRegular"
                >
                  {iconText}
                </Typography>
              </BankInfoWrapper>
              {isCryptoTransaction ? (
                <CryptoTransactionCenterTextWrapper>
                  <CryptoTransactionCenterText
                    type="bodyButton"
                    fontFamily="primaryBold"
                  >
                    {destinationAddress}
                  </CryptoTransactionCenterText>
                </CryptoTransactionCenterTextWrapper>
              ) : (
                <CenterText type="subHeadline" fontFamily="primaryBold">
                  {iban}
                </CenterText>
              )}
            </CardWrapper>
          </Card>
        )}
        <SaveTextWrapper>
          <Typography
            fontWeight={500}
            type="bodyButton"
            fontFamily="primaryMedium"
          >
            {mainDescription}
          </Typography>
        </SaveTextWrapper>

        <ActionsContainer>
          <Button
            size="md"
            title={t('result_feedback_no_need')}
            buttonType="secondary"
            onClick={onCancel}
          />
          <Button size="md" onClick={handleSave} title={modalTitle} />
        </ActionsContainer>
      </TransactionContainer>
      {/* </LinearGradient> */}

      <Dialog title={modalTitle}>
        <ModalContent>
          <EditItemNameModalContent
            autoFocus
            image={image}
            isLoading={loading}
            value={addressName}
            title={modalDescription}
            isDisabled={!addressName}
            buttonTitle={t('common_save')}
            placeholder={t('common_name')}
            subTitle={iban || destinationAddress || ''}
            onConfirm={handleSaveTransaction}
            onChangeText={setAddressName}
          />
        </ModalContent>
      </Dialog>
    </GradientWrapper>
  );
};

export default FeedbackAdditionalAction;
